import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import ContratistaSelector from './ContratistaSelector';

const MANUAL_FORM_INICIAL = {
    entidad_id: '',
    tipo_documento_id: '',
    fecha_otorgamiento: '',
    fecha_inicio_reposo: '',
    dias_reposo: '',
    actividad_laboral: '',
    ocupacion: '',
    regimen_previsional: '',
    institucion_codigo: '',
    institucion_letra: '',
    institucion_nombre: '',
    calidad_trabajador: '',
    afiliado_afc: '',
    contrato_indefinido: '',
    fecha_primera_afiliacion: '',
    fecha_contrato: '',
    entidad_pagadora: '',
    entidad_pagadora_nombre: ''
};

const inputStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '6px',
    border: '1px solid #cbd5e1',
    fontSize: '1rem',
    boxSizing: 'border-box'
};

// En filas de grilla, las etiquetas pueden ocupar 1 o 2 líneas;
// la altura mínima uniforme alinea los inputs/selects en la misma línea.
const labelGridStyle = {
    display: 'block',
    minHeight: '2.6em',
    marginBottom: '4px'
};

export default function LicenciasManager({ usuario, initialFilter = null }) {
    const [licenciasData, setLicenciasData] = useState([]);
    const [allPersonal, setAllPersonal] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [selectedPersonForUpload, setSelectedPersonForUpload] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [tiposLicencias, setTiposLicencias] = useState([]);
    const [selectedTipoLicencia, setSelectedTipoLicencia] = useState('');
    const [uploadingDoc, setUploadingDoc] = useState(null);

    // Vista mandante (solo lectura) con selector de contratista
    const [contratistas, setContratistas] = useState([]);
    const [selectedContratista, setSelectedContratista] = useState('');

    // Ingreso manual
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualForm, setManualForm] = useState(MANUAL_FORM_INICIAL);
    const [manualFile, setManualFile] = useState(null);
    const [savingManual, setSavingManual] = useState(false);
    const [catalogos, setCatalogos] = useState({});

    // Filtro proveniente del drill-down del dashboard ('12m' | 'vigentes' | null)
    const [filtro, setFiltro] = useState(initialFilter);

    useEffect(() => {
        setFiltro(initialFilter || null);
    }, [initialFilter]);

    const isMandante = !usuario.id_contratista && usuario.tipo !== 'C';
    const puedeCrear = usuario.tipo === 'C' && !!usuario.id_contratista;
    const targetContratistaId = usuario.id_contratista || selectedContratista;

    useEffect(() => {
        if (isMandante) {
            setLoading(false);
            fetchContratistas();
        }
    }, [isMandante]);

    useEffect(() => {
        if (targetContratistaId) {
            fetchAllPersonal();
            fetchTiposLicencias();
            fetchAllLicencias();
            if (puedeCrear) fetchCatalogos();
        } else if (isMandante && filtro && contratistas.length > 0) {
            // Drill-down desde el dashboard: sin contratista seleccionado,
            // mostrar licencias de todos los contratistas de la empresa
            fetchAllLicencias();
        }
    }, [targetContratistaId, isMandante, filtro, contratistas]);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchContratistas = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`);
            if (response.ok) {
                setContratistas(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar contratistas:', err);
        }
    };

    const fetchCatalogos = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/licencias/catalogos`);
            if (response.ok) {
                setCatalogos(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar catálogos de licencias:', err);
        }
    };

    const fetchAllPersonal = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/personal-contratistas/${targetContratistaId}`);
            if (response.ok) {
                setAllPersonal(await response.json());
            } else {
                setError('Error al cargar la nómina de personal');
            }
        } catch (err) {
            setError('Error de conexión al cargar personal');
        }
    };

    const fetchAllLicencias = async () => {
        setLoading(true);
        try {
            // Mandante sin selección (drill-down): abarca todos los contratistas
            const contratistaIds = isMandante && !selectedContratista
                ? contratistas.map(c => c.id)
                : [targetContratistaId];

            const allLicencias = [];

            for (const cid of contratistaIds) {
                const personal = await fetchWithAuth(`${API_BASE_URL}/api/personal-contratistas/${cid}`);
                if (!personal.ok) continue;
                const personalList = await personal.json();

                for (const person of personalList) {
                    try {
                        const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos-cargados/entidad/P/${person.id}`);
                        if (response.ok) {
                            const docs = await response.json();
                            // Filtrar solo documentos con clase='LICMED'
                            docs.forEach(doc => {
                                if (doc.clase === 'LICMED') {
                                    allLicencias.push({
                                        ...doc,
                                        persona_id: person.id,
                                        persona_dni: person.dni,
                                        persona_nombre: person.nombre,
                                        persona_apellido: person.apellido
                                    });
                                }
                            });
                        }
                    } catch (err) {
                        console.error('Error al cargar documentos para persona:', person.id);
                    }
                }
            }

            setLicenciasData(allLicencias);
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    const fetchTiposLicencias = async () => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos?empresa_id=${usuario.empresa_id}&clase=LICMED`);
            if (response.ok) {
                setTiposLicencias(await response.json());
            }
        } catch (err) {
            console.error('Error al cargar tipos de licencias:', err);
        }
    };

    const handleUploadLicencia = async () => {
        if (!selectedPersonForUpload || !uploadFile || !selectedTipoLicencia) {
            alert('Por favor selecciona persona, tipo de licencia y archivo');
            return;
        }

        const tipoLicencia = tiposLicencias.find(t => t.id === selectedTipoLicencia);
        setUploadingDoc(selectedTipoLicencia);

        const data = new FormData();
        data.append('archivo', uploadFile);
        data.append('titulo', `${tipoLicencia.tipo_documento} - ${selectedPersonForUpload.nombre} ${selectedPersonForUpload.apellido}`);
        data.append('tipo_documento_id', selectedTipoLicencia);
        data.append('id_contratista', targetContratistaId);
        data.append('entidad_id', selectedPersonForUpload.id);
        data.append('entidad_tipo', 'P');

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Licencia médica cargada y analizada con éxito');
                setShowUploadModal(false);
                setSelectedPersonForUpload(null);
                setUploadFile(null);
                setSelectedTipoLicencia('');
                fetchAllLicencias();
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al subir');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUploadingDoc(null);
        }
    };

    const setManualField = (campo, valor) => {
        setManualForm(prev => ({ ...prev, [campo]: valor }));
    };

    const handleGuardarManual = async () => {
        if (!manualForm.entidad_id || !manualForm.tipo_documento_id || !manualForm.fecha_otorgamiento ||
            !manualForm.fecha_inicio_reposo || !manualForm.dias_reposo) {
            alert('Debe completar: persona, tipo de licencia, fecha de otorgamiento, fecha de inicio de reposo y días de reposo');
            return;
        }

        setSavingManual(true);
        const data = new FormData();
        Object.entries(manualForm).forEach(([k, v]) => data.append(k, v));
        if (manualFile) data.append('archivo', manualFile);

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/licencias-manuales`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Licencia médica ingresada correctamente');
                setShowManualModal(false);
                setManualForm(MANUAL_FORM_INICIAL);
                setManualFile(null);
                fetchAllLicencias();
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al ingresar la licencia');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setSavingManual(false);
        }
    };

    const renderSelectCatalogo = (campo, catalogo, label, conGrupos = false) => {
        const opciones = catalogos[catalogo] || [];
        return (
            <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>{label}</label>
                <select
                    value={manualForm[campo]}
                    onChange={(e) => setManualField(campo, e.target.value)}
                    style={inputStyle}
                >
                    <option value="">Seleccione...</option>
                    {conGrupos ? (
                        <>
                            <optgroup label="Subsidio licencia tipo 1-2-3-4 ó 7">
                                {opciones.filter(o => o.grupo === '1-7').map(o => (
                                    <option key={o.codigo} value={o.codigo}>{o.codigo} = {o.descripcion}</option>
                                ))}
                            </optgroup>
                            <optgroup label="Subsidio licencia tipo 5 ó 6">
                                {opciones.filter(o => o.grupo === '5-6').map(o => (
                                    <option key={o.codigo} value={o.codigo}>{o.codigo} = {o.descripcion}</option>
                                ))}
                            </optgroup>
                        </>
                    ) : (
                        opciones.map(o => (
                            <option key={o.codigo} value={o.codigo}>{o.codigo} = {o.descripcion}</option>
                        ))
                    )}
                </select>
            </div>
        );
    };

    const filteredLicencias = licenciasData.filter(lic => {
        const coincideBusqueda =
            lic.persona_dni?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lic.persona_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lic.persona_apellido?.toLowerCase().includes(searchTerm.toLowerCase());
        if (!coincideBusqueda) return false;

        if (filtro === 'vigentes') {
            // Coincide con el criterio del indicador: valid y vencimiento > hoy
            return lic.estado === 'APROBADO' && lic.vencimiento && new Date(lic.vencimiento) > new Date();
        }
        if (filtro === '12m') {
            const hace12m = new Date();
            hace12m.setMonth(hace12m.getMonth() - 12);
            return lic.fecha && new Date(lic.fecha) >= hace12m;
        }
        return true;
    });

    return (
        <div className="manager-container">
            {isMandante && (
                <div style={{ marginBottom: '20px' }}>
                    <ContratistaSelector
                        contratistas={contratistas}
                        value={selectedContratista}
                        onChange={setSelectedContratista}
                        label="Contratista"
                    />
                    {!selectedContratista && !filtro && (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '12px' }}>
                            Seleccione un contratista para ver sus licencias médicas.
                        </p>
                    )}
                </div>
            )}

            <div className="header-actions" style={{ marginBottom: '24px', justifyContent: 'space-between' }}>
                <div style={{ position: 'relative', flex: 1, marginRight: '12px' }}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar por DNI, nombre o apellido..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 20px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--border-light)',
                            background: '#ffffff',
                            fontSize: '1rem',
                            boxShadow: 'var(--shadow-sm)',
                            transition: 'all 0.2s ease'
                        }}
                        className="search-input"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{
                                position: 'absolute',
                                right: '15px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                color: 'var(--text-muted)',
                                fontSize: '1.2rem'
                            }}
                        >
                            &times;
                        </button>
                    )}
                </div>
                {puedeCrear && (
                    <>
                        <button onClick={() => { setManualForm(MANUAL_FORM_INICIAL); setManualFile(null); setShowManualModal(true); }} className="add-button" style={{ whiteSpace: 'nowrap', marginRight: '8px' }}>
                            ✍️ Ingreso Manual
                        </button>
                        <button onClick={() => setShowUploadModal(true)} className="add-button" style={{ whiteSpace: 'nowrap' }}>
                            + Cargar Licencia
                        </button>
                    </>
                )}
            </div>

            {filtro && (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '16px',
                    padding: '8px 14px',
                    borderRadius: '20px',
                    background: filtro === 'vigentes' ? 'rgba(14, 165, 233, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                    border: `1px solid ${filtro === 'vigentes' ? '#0ea5e9' : '#10b981'}`,
                    color: filtro === 'vigentes' ? '#0369a1' : '#047857',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                }}>
                    <span>
                        {filtro === 'vigentes' ? '✅ Mostrando solo licencias vigentes' : '🏥 Mostrando licencias de los últimos 12 meses'}
                    </span>
                    <button
                        onClick={() => setFiltro(null)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}
                        title="Quitar filtro"
                    >
                        ✕
                    </button>
                </div>
            )}

            {error && <div className="error-message" style={{ marginBottom: '20px' }}>{error}</div>}

            {showUploadModal && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '600px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', margin: 0 }}>
                                📤 Cargar Nueva Licencia Médica
                            </h3>
                            <button onClick={() => { setShowUploadModal(false); setSelectedPersonForUpload(null); setUploadFile(null); setSelectedTipoLicencia(''); }} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                            <label>Seleccionar Persona</label>
                            <select
                                value={selectedPersonForUpload?.id || ''}
                                onChange={(e) => {
                                    const person = allPersonal.find(p => p.id === e.target.value);
                                    setSelectedPersonForUpload(person);
                                }}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '6px',
                                    border: '1px solid #cbd5e1',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="">Seleccione una persona...</option>
                                {allPersonal.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.nombre} {p.apellido} ({p.dni})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {selectedPersonForUpload && (
                            <>
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label>Tipo de Licencia Médica</label>
                                    <select
                                        value={selectedTipoLicencia}
                                        onChange={(e) => setSelectedTipoLicencia(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: '1px solid #cbd5e1',
                                            fontSize: '1rem'
                                        }}
                                    >
                                        <option value="">Seleccione tipo de licencia...</option>
                                        {tiposLicencias.map(tipo => (
                                            <option key={tipo.id} value={tipo.id}>
                                                {tipo.tipo_documento}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedTipoLicencia && (
                                    <div className="form-group" style={{ marginBottom: '20px' }}>
                                        <label>Archivo de Licencia Médica</label>
                                        <input
                                            type="file"
                                            accept=".pdf,.png,.jpg,.jpeg"
                                            onChange={(e) => setUploadFile(e.target.files[0])}
                                            style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '6px',
                                                border: '1px solid #cbd5e1',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        {uploadFile && (
                                            <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--primary)' }}>
                                                ✓ {uploadFile.name}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        <div className="form-actions" style={{ marginTop: '32px' }}>
                            <button type="button" onClick={() => { setShowUploadModal(false); setSelectedPersonForUpload(null); setUploadFile(null); setSelectedTipoLicencia(''); }} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cancelar
                            </button>
                            <button 
                                onClick={handleUploadLicencia}
                                disabled={!selectedPersonForUpload || !uploadFile || !selectedTipoLicencia || uploadingDoc !== null}
                                className="add-button"
                                style={{ padding: '12px 40px' }}
                            >
                                {uploadingDoc ? (
                                    <>
                                        <span className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px', marginRight: '8px' }}></span>
                                        Cargando...
                                    </>
                                ) : 'Cargar Licencia'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showManualModal && (
                <div className="modal">
                    <div className="standard-form" style={{ width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <h3 style={{ color: 'var(--sidebar-bg)', fontSize: '1.5rem', margin: 0 }}>
                                ✍️ Ingreso Manual de Licencia Médica
                            </h3>
                            <button onClick={() => { setShowManualModal(false); setManualForm(MANUAL_FORM_INICIAL); setManualFile(null); }} className="btn-secondary" style={{ padding: '8px' }}>✕</button>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Persona *</label>
                            <select
                                value={manualForm.entidad_id}
                                onChange={(e) => setManualField('entidad_id', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Seleccione una persona...</option>
                                {allPersonal.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.nombre} {p.apellido} ({p.dni})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Tipo de Licencia Médica *</label>
                            <select
                                value={manualForm.tipo_documento_id}
                                onChange={(e) => setManualField('tipo_documento_id', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Seleccione tipo de licencia...</option>
                                {tiposLicencias.map(tipo => (
                                    <option key={tipo.id} value={tipo.id}>
                                        {tipo.tipo_documento}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '8px' }}>
                            <div className="form-group">
                                <label style={labelGridStyle}>Fecha otorgamiento *</label>
                                <input type="date" value={manualForm.fecha_otorgamiento} onChange={(e) => setManualField('fecha_otorgamiento', e.target.value)} style={inputStyle} />
                            </div>
                            <div className="form-group">
                                <label style={labelGridStyle}>Fecha inicio reposo *</label>
                                <input type="date" value={manualForm.fecha_inicio_reposo} onChange={(e) => setManualField('fecha_inicio_reposo', e.target.value)} style={inputStyle} />
                            </div>
                            <div className="form-group">
                                <label style={labelGridStyle}>Días de reposo *</label>
                                <input type="number" min="1" value={manualForm.dias_reposo} onChange={(e) => setManualField('dias_reposo', e.target.value)} style={inputStyle} />
                            </div>
                        </div>

                        <h4 style={{ margin: '16px 0 8px', color: 'var(--sidebar-bg)' }}>Actividad laboral del trabajador</h4>
                        {renderSelectCatalogo('actividad_laboral', 'actividad_laboral', 'Actividad laboral')}
                        {renderSelectCatalogo('ocupacion', 'ocupacion', 'Ocupación')}

                        <h4 style={{ margin: '16px 0 8px', color: 'var(--sidebar-bg)' }}>Régimen previsional</h4>
                        {renderSelectCatalogo('regimen_previsional', 'regimen_previsional', 'Régimen previsional')}

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Código institución</label>
                                <input type="text" maxLength="2" value={manualForm.institucion_codigo} onChange={(e) => setManualField('institucion_codigo', e.target.value)} style={inputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Letra (caja prev.)</label>
                                <input type="text" maxLength="1" value={manualForm.institucion_letra} onChange={(e) => setManualField('institucion_letra', e.target.value)} style={inputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '16px', gridColumn: '1 / -1' }}>
                                <label>Nombre institución previsional</label>
                                <input type="text" value={manualForm.institucion_nombre} onChange={(e) => setManualField('institucion_nombre', e.target.value)} style={inputStyle} placeholder="Ej: PLANVITAL" />
                            </div>
                        </div>

                        {renderSelectCatalogo('calidad_trabajador', 'calidad_trabajador', 'Calidad del trabajador')}

                        <h4 style={{ margin: '16px 0 8px', color: 'var(--sidebar-bg)' }}>Seguro de desempleo</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Trabajador afiliado a AFC</label>
                                <select value={manualForm.afiliado_afc} onChange={(e) => setManualField('afiliado_afc', e.target.value)} style={inputStyle}>
                                    <option value="">Seleccione...</option>
                                    <option value="1">1 = SÍ</option>
                                    <option value="2">2 = NO</option>
                                </select>
                            </div>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Contrato de duración indefinida</label>
                                <select value={manualForm.contrato_indefinido} onChange={(e) => setManualField('contrato_indefinido', e.target.value)} style={inputStyle}>
                                    <option value="">Seleccione...</option>
                                    <option value="1">1 = SÍ</option>
                                    <option value="2">2 = NO</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Fecha primera afiliación entidad previsional</label>
                                <input type="date" value={manualForm.fecha_primera_afiliacion} onChange={(e) => setManualField('fecha_primera_afiliacion', e.target.value)} style={inputStyle} />
                            </div>
                            <div className="form-group" style={{ marginBottom: '16px' }}>
                                <label style={labelGridStyle}>Fecha de contrato de trabajo</label>
                                <input type="date" value={manualForm.fecha_contrato} onChange={(e) => setManualField('fecha_contrato', e.target.value)} style={inputStyle} />
                            </div>
                        </div>

                        <h4 style={{ margin: '16px 0 8px', color: 'var(--sidebar-bg)' }}>Entidad que paga el subsidio</h4>
                        {renderSelectCatalogo('entidad_pagadora', 'entidad_pagadora', 'Entidad pagadora del subsidio', true)}
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Nombre entidad pagadora del subsidio</label>
                            <input type="text" value={manualForm.entidad_pagadora_nombre} onChange={(e) => setManualField('entidad_pagadora_nombre', e.target.value)} style={inputStyle} placeholder="Ej: COMPIN" />
                        </div>

                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Archivo escaneado (opcional)</label>
                            <input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => setManualFile(e.target.files[0])}
                                style={inputStyle}
                            />
                            {manualFile && (
                                <div style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--primary)' }}>
                                    ✓ {manualFile.name}
                                </div>
                            )}
                        </div>

                        <div className="form-actions" style={{ marginTop: '24px' }}>
                            <button type="button" onClick={() => { setShowManualModal(false); setManualForm(MANUAL_FORM_INICIAL); setManualFile(null); }} className="btn-secondary" style={{ marginRight: 'auto' }}>
                                Cancelar
                            </button>
                            <button
                                onClick={handleGuardarManual}
                                disabled={savingManual}
                                className="add-button"
                                style={{ padding: '12px 40px' }}
                            >
                                {savingManual ? (
                                    <>
                                        <span className="spinner" style={{ width: '12px', height: '12px', borderWidth: '2px', marginRight: '8px' }}></span>
                                        Guardando...
                                    </>
                                ) : 'Guardar Licencia'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && licenciasData.length === 0 ? <p>Cargando licencias médicas...</p> : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>DNI / RUT</th>
                            <th>Nombre Completo</th>
                            <th>Estado</th>
                            <th>Fecha Carga</th>
                            <th>Vencimiento</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLicencias.length > 0 ? (
                            filteredLicencias.map(lic => (
                                <tr key={lic.documento_id || `${lic.persona_id}-${lic.tipo_id}`}>
                                    <td data-label="DNI / RUT" style={{ fontWeight: '700', color: 'var(--primary)' }}>
                                        {lic.persona_dni}
                                    </td>
                                    <td data-label="Nombre Completo">
                                        <div style={{ fontWeight: '600' }}>
                                            {lic.persona_nombre} {lic.persona_apellido}
                                        </div>
                                    </td>
                                    <td data-label="Estado">
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <span className={`status-badge ${lic.estado.toLowerCase()}`} style={{ fontSize: '0.7rem', display: 'inline-block', width: 'fit-content' }}>
                                                {lic.estado}
                                            </span>
                                            {lic.detalles && (
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic', maxWidth: '300px' }}>
                                                    {lic.detalles}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td data-label="Fecha Carga" style={{ fontSize: '0.85rem' }}>
                                        {lic.fecha ? new Date(lic.fecha).toLocaleDateString() : '---'}
                                    </td>
                                    <td data-label="Vencimiento" style={{ 
                                        fontSize: '0.85rem', 
                                        color: lic.estado === 'VENCIDO' ? '#ea580c' : 'inherit', 
                                        fontWeight: lic.estado === 'VENCIDO' ? '600' : '400' 
                                    }}>
                                        {lic.vencimiento ? new Date(lic.vencimiento).toLocaleDateString() : '---'}
                                    </td>
                                    <td data-label="Acciones">
                                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                            {lic.nombre_archivo && (
                                                <a
                                                    href={`${API_BASE_URL}/uploads/${lic.nombre_archivo}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="btn-action btn-details"
                                                    style={{ fontSize: '0.75rem', padding: '6px 12px', textDecoration: 'none' }}
                                                    title="Descargar documento"
                                                >
                                                    📂 Descargar
                                                </a>
                                            )}
                                            {puedeCrear && (
                                                <button
                                                    onClick={() => {
                                                        setSelectedPersonForUpload(allPersonal.find(p => p.id === lic.persona_id));
                                                        setShowUploadModal(true);
                                                    }}
                                                    className="btn-action btn-edit"
                                                    style={{ fontSize: '0.75rem', padding: '6px 12px' }}
                                                    title="Cargar nueva licencia para esta persona"
                                                >
                                                    📤 Licencia
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>
                                    {licenciasData.length === 0 ? 'No hay licencias médicas registradas.' : 'No se encontraron licencias que coincidan con la búsqueda.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
